import {clamp} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {
  MAX_ITEM_WIDTH,
  MIN_ITEM_WIDTH,
} from 'notebook/notes/groups/note-part-grid/NotePartGridGroupConstants';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import * as logging from 'util/Logging';

export interface DraggableFlowItem {
  notePart: NotePart;
  computedWidth: number;
}

/**
 * containerWidth
 * ├─────────────────────────────────────────────────────────────────────────────────────────────────┤
 *
 * ┌───────────────────────────┐       ┌───────────────────────────┐       ┌───────────────────────────┐
 * │                           │       │                           │       │                           │
 * │     ┌───────────────┐     │       │     ┌───────────────┐     │       │     ┌───────────────┐     │
 * │     │               │     │       │     │               │     │       │     │               │     │
 * │     │   notePart    │     │       │     │   notePart    │     │       │     │    notePart   │     │
 * │     │   content     │     │       │     │   content     │     │       │     │    content    │     │
 * │     │               │     │       │     │               │     │       │     │               │     │
 * │     └───────────────┘     │       │     └───────────────┘     │       │     └───────────────┘     │
 * │                           │       │                           │       │                           │
 * └───────────────────────────┘       └───────────────────────────┘       └───────────────────────────┘
 *
 *      │←notePartWidth→│                   │←notePartWidth→│                   │←notePartWidth→│
 * │←pad→│               │←pad→│
 * │←───── totalItemWidth ────→│       │←───── totalItemWidth ────→│       │←──── totalItemWidth ─────→│
 * (notePartWidth + padding*2)
 *                            │←─gap─→│                           │←─gap─→│
 *
 * │←───── totalItemWidth ────→│←─gap─→│←───── totalItemWidth ────→│←─gap─→│←──── totalItemWidth ─────→│
 *
 * ** "pad" represents the padding on one side. The padding parameter adds pad to each side (padding * 2 total).
 *
 * ===========================================================================================================
 *
 *                    minWidth                                    maxWidth
 *                       │                                           │
 *                       ▼                                           ▼
 * ├──────────────────────┼───────────────────────────────────────────┼──────────────────────────────┤
 *                       │←─────── valid range for totalItemWidth ──→│
 *
 *                       Also cannot exceed containerWidth (hard cap)
 *
 * ===========================================================================================================
 * ***NUMERIC EXAMPLE***
 *
 * containerWidth = 800px
 * minWidth = 150px, maxWidth = 400px
 * padding = 10px (10px per side, 20px total), gap = 30px
 *
 * Given: notePart.itemWidth = 180px
 *
 * ┌─────────────────────────────┐
 * │                             │
 * │      ┌───────────────┐      │
 * │      │               │      │
 * │      │   180px wide  │      │   ← notePartWidth (from notePart.itemWidth)
 * │      │    content    │      │
 * │      │               │      │
 * │      └───────────────┘      │
 * │                             │
 * └─────────────────────────────┘
 *
 * │10px │←───── 180px ─────→│10px│
 * │←pad→│←─ notePartWidth ──→│←pad→│
 * │←──────── 200px ────────────→│   ← totalItemWidth = 180 + 20 = 200px
 *                                   (within [150, 400] ✓)
 *
 */

export function computeRowsForFlowLayout(
  noteParts: Array<NotePart>,
  containerWidth: number,
  minWidth: number = MIN_ITEM_WIDTH,
  maxWidth: number = MAX_ITEM_WIDTH,
  padding: number = 0,
  gap: number = 0,
  previousItems?: Record<string, DraggableFlowItem>
): Array<Array<DraggableFlowItem>> {
  const rows: Array<Array<DraggableFlowItem>> = [];
  let currentRow: Array<DraggableFlowItem> = [];
  rows.push(currentRow); // DnDCanvas needs at least one row
  let currentRowWidth = 0;

  assert(noteParts.length > 0, 'NoteParts array must not be empty');

  for (const notePart of noteParts) {
    if (notePart.itemWidth == null) {
      logging.warn('NotePart missing itemWidth, defaulting to maxWidth', {
        extra: {notePart},
      });
    }
    // Account for note part width + extra width for wrapper (padding*2), if provided
    const notePartWidth = notePart.itemWidth ?? maxWidth - padding * 2;
    // Clamp the total item width to fall within the min and max width bounds, and ensure it is not greater than the container width.
    const totalItemWidth = Math.min(clamp(minWidth, maxWidth, notePartWidth + padding * 2), containerWidth);
    // Calculate width needed to fit the current note part into the current row, need to account for gap between items if there is at least one item already in the current row
    const neededWidth = currentRow.length > 0 ? totalItemWidth + gap : totalItemWidth;

    const cached = previousItems?.[notePart.cid];
    const item =
      cached != null && cached.notePart === notePart && cached.computedWidth === totalItemWidth
        ? cached
        : {notePart, computedWidth: totalItemWidth};

    // If the current row cannot accommodate the current note part, start a new row and insert the current note part
    if (currentRowWidth + neededWidth > containerWidth && currentRow.length > 0) {
      currentRow = [item];
      rows.push(currentRow);
      currentRowWidth = totalItemWidth;
    } else {
      // Otherwise, add the current note part to the current row
      assert(neededWidth <= containerWidth - currentRowWidth, 'Item should always fit in row at this point');
      currentRow.push(item);
      currentRowWidth += neededWidth;
    }
  }

  return rows;
}
